import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import { isEmpty } from "lodash";
import dayjs from "dayjs";

import { actions, selectors } from "rdx";
import useRestrictAccessModal from "hooks/useRestrictAccessModal";
import { useSmartForm } from "hooks/useSmartForm";
import useWindowResize from "hooks/useWindowResize";
import useRouteMatcher from "hooks/useRouteMatcher";
import { boolToString, convertToBool, stringToBool } from "lib/helpers";
import { CustomFieldT } from "types/Project/interfaces";

import { mountingOptions, existingSystemOwnershipOptions } from "lib/misc/dropdownOptions";
import Banners from "components/Banners";
import { createBannerDetails } from "components/Banners/helpers";
import SmartForm from "components/SmartForm";
import { RightCaretIcon, DownCaretThicker } from "components/Icons";
import MapDisplay from "components/MapDisplay";
import SingleFileDragAndDrop from "components/FileUploader/SingleFileDragAndDrop";
import SmartFormTextField from "components/mui-styled-components/SmartFormElements/SmartFormTextField";
import SmartFormTextarea from "components/mui-styled-components/SmartFormElements/SmartFormTextarea";
import SmartFormDropdown from "components/mui-styled-components/SmartFormElements/SmartFormDropdown";
import SmartFormDatePicker from "components/mui-styled-components/SmartFormElements/SmartFormDatePicker";
import CalendarInput from "components/mui-styled-components/SmartFormElements/SmartFormDatePicker/CalendarInput";
import { theme } from "styles/themes";
import EnterpriseCustomFields from "containers/Sales/projects/components/EnterpriseCustomFields";
import { SmartFormRadioGroup } from "@mui-styled-components/SmartFormElements";
import { isNull, isUndefined, isNullish } from "types/guards";
import { StyledSectionLabel } from "../LeadSummaryForm/styles";
import { SiteInfoFormT, schema, blankFormData, yesOrNoOptions } from "./SiteInfoFormData";

import * as S from "./styles";

export type SiteInfoFormPropsT = {
  latestMessage: { message: string; type: string };
  submitRef: React.LegacyRef<HTMLButtonElement>;
  saveRef: React.LegacyRef<HTMLButtonElement>;
  currentTab: number;
  customFields: CustomFieldT[];
  powurFields: CustomFieldT[];
  enterpriseName?: string;
  setCurrentTab: (tabIdx: number) => void;
  setMissingQualifyingSiteInfo: (bool: boolean) => void;
  setIsFormSubmitting: (bool: boolean) => void;
  setIsFormTouched: (bool: boolean) => void;
};

type UtilityCompanyT = {
  id: number;
  displayName: string;
  externalName: string;
  state: string;
};

const SiteInfoForm = ({
  latestMessage,
  submitRef,
  saveRef,
  currentTab,
  setCurrentTab,
  setMissingQualifyingSiteInfo,
  setIsFormSubmitting,
  setIsFormTouched,
  customFields,
  powurFields,
  enterpriseName,
}: SiteInfoFormPropsT) => {
  const { restrictAccessModal, isRestrictedUser } = useRestrictAccessModal();

  const dispatch = useDispatch();
  const utilities = useSelector(selectors.selectLeadToProposalUtilities);
  const leadDetails = useSelector(selectors.selectLeadToProposalLeadDetails);
  const { constructionDetails } = useSelector(selectors.selectLeadToProposalProjectDetails);
  const { siteInfo, showThdBanner, isQualifiable, isQualified } = leadDetails;
  const { isEnterprise, isAdmin } = useRouteMatcher();
  const { isMobileWidth } = useWindowResize();

  const [isImageUploadOpen, setIsImageUploadOpen] = useState<boolean>(false);
  const [hasBlueHighlightOnKwh, setHasBlueHighlightOnKwh] = useState<boolean>(
    !isQualified && !leadDetails.siteInfo.avgMonthlyBill,
  );

  const { methods, getSmartFormProps } = useSmartForm<SiteInfoFormT>({
    schema,
    useFormProps: { defaultValues: blankFormData },
  });

  const {
    watch,
    reset,
    trigger,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const values = watch();
  const highlightEnergyUsageInput = !isQualified && (!values.kwhConsumption || !values.avgMonthlyBill);

  const utilityChoices = useMemo(
    () =>
      utilities.map((u: UtilityCompanyT) => ({
        value: u.externalName,
        label: u.displayName,
        key: u.id,
      })),
    [utilities],
  );

  useEffect(() => {
    if (!isEmpty(siteInfo) && utilityChoices.length && !!constructionDetails) {
      reset({
        utilityBillUrl: leadDetails?.utilityBillUrl || null,
        utilityBillSize: siteInfo.utilityBillInfo?.fileSize || 0,
        utilityCompany: siteInfo.utilityCompany || "",
        kwhConsumption: siteInfo.kwhConsumption || "",
        avgMonthlyBill: siteInfo.avgMonthlyBill?.toString() || "",
        mounting: siteInfo.mounting || "roof",
        hasExistingSystem: !isUndefined(leadDetails?.existingSystem?.hasExistingSystem)
          ? boolToString(leadDetails?.existingSystem?.hasExistingSystem)
          : null,
        existingSystemOwnership: leadDetails?.existingSystem?.ownership ?? null,
        isNewConstruction: isNullish(constructionDetails?.isNewConstruction)
          ? null
          : convertToBool(constructionDetails.isNewConstruction),
        detailsOfConstruction: constructionDetails.detailsOfConstruction || null,
        estimatedDateTo: constructionDetails.estimatedDateTo
          ? dayjs(constructionDetails.estimatedDateTo).format("YYYY-MM-DD")
          : null,
      });
    }
  }, [leadDetails.siteInfo, utilityChoices, constructionDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsFormTouched(isDirty);
  }, [isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const missingInfoForQualification =
      !values.utilityCompany ||
      !values.hasExistingSystem ||
      (stringToBool(values.hasExistingSystem) && !values.existingSystemOwnership) ||
      !leadDetails?.isAddressConfirmed ||
      (!values.kwhConsumption && !values.avgMonthlyBill) ||
      isNull(values.isNewConstruction);
    setMissingQualifyingSiteInfo(missingInfoForQualification);
  }, [
    values.utilityCompany,
    values.hasExistingSystem,
    values.existingSystemOwnership,
    leadDetails?.isAddressConfirmed,
    values.kwhConsumption,
    values.avgMonthlyBill,
    values.isNewConstruction,
    setMissingQualifyingSiteInfo,
  ]);

  const renderInput = (timeStamp) => {
    const formattedTime = timeStamp ? dayjs(timeStamp).format("MM/DD/YYYY") : "";
    return <CalendarInput name="estimatedDateTo" value={formattedTime} />;
  };

  const handleSelectDate = (value) => {
    setValue("estimatedDateTo", value);
  };

  const formatFormValues = () => {
    return {
      leadId: leadDetails.id,
      lead: {
        firstName: leadDetails.firstName,
        lastName: leadDetails.lastName,
        email: leadDetails.email,
        phone: leadDetails.phone,
        locale: leadDetails.locale,
        leadSource: leadDetails.leadSource,
        category: leadDetails.category,
        copilot: leadDetails.copilot,
        notes: leadDetails.notes,
      },
      address: leadDetails.address,
      siteInfoValues: {
        utilityBillUrl: values.utilityBillUrl || null,
        utilityBillSize: values.utilityBillSize || null,
        utilityCompany: values.utilityCompany || null,
        mounting: values.mounting,
        kwhConsumption: values.kwhConsumption || null,
        avgMonthlyBill: values.avgMonthlyBill ? parseInt(values.avgMonthlyBill, 10) : null,
        constructionDetails: {
          isNewConstruction: values.isNewConstruction ? convertToBool(values.isNewConstruction) : null,
          ...(convertToBool(values.isNewConstruction)
            ? {
                detailsOfConstruction: values.detailsOfConstruction || null,
                estimatedDateTo: values.estimatedDateTo ? dayjs(values.estimatedDateTo).format("YYYY-MM-DD") : null,
              }
            : {}),
        },
      },
    };
  };

  const handleQualifyLead = () => {
    if (isRestrictedUser) {
      restrictAccessModal();
    } else if (isQualifiable) {
      trigger().then((validForm) => {
        if (validForm) {
          const { leadId, lead, siteInfoValues, address } = formatFormValues();
          setIsFormSubmitting(true);
          dispatch(
            actions.setAlertMessageVisible({
              message: "Submitting. . .",
              severity: "info",
              duration: 6000,
            }),
          );
          if (isDirty) {
            dispatch(
              actions.saveAndSubmitLead({
                leadId,
                lead,
                siteInfo: siteInfoValues,
                address,
                enterprise: isEnterprise,
                admin: isAdmin,
                existingSystem: !isNull(values.hasExistingSystem) && {
                  hasExistingSystem: values.hasExistingSystem,
                  ...(stringToBool(values.hasExistingSystem) && {
                    ownership: values.existingSystemOwnership,
                  }),
                },
              }),
            );
          } else {
            dispatch(
              actions.submitLeadToProvider({
                leadId,
                enterprise: isEnterprise,
                admin: isAdmin,
              }),
            );
          }
        }
      });
    }
  };

  const handleUpdateLead = () => {
    trigger().then((validForm) => {
      if (validForm) {
        const { leadId, lead, siteInfoValues, address } = formatFormValues();
        setIsFormSubmitting(true);
        dispatch(
          actions.setAlertMessageVisible({
            message: "Submitting. . .",
            severity: "info",
            duration: 6000,
          }),
        );
        dispatch(
          actions.updateLead({
            leadId,
            lead,
            siteInfo: siteInfoValues,
            existingSystem: !isNull(values.hasExistingSystem) && {
              hasExistingSystem: values.hasExistingSystem,
              ...(stringToBool(values.hasExistingSystem) && {
                ownership: values.existingSystemOwnership,
              }),
            },
            address,
            enterprise: isEnterprise,
            admin: isAdmin,
          }),
        );
      }
    });
  };

  const handleSiteImageUpload = (fileUrl: string): void => {
    if (fileUrl) {
      dispatch(
        actions.createLeadAsset({
          leadId: leadDetails.id,
          fileUrl,
          assetType: "custom_design",
        }),
      );
    }
    setValue("customSiteImage", fileUrl);
  };

  const onSubmit = (): void => {
    if (!isQualified) {
      handleQualifyLead();
    } else if (isDirty) {
      handleUpdateLead();
    } else {
      setCurrentTab(currentTab + 1);
    }
  };

  const onError = (e) => {
    dispatch(
      actions.setAlertMessageVisible({
        message: `Invalid Fields: ${Object.keys(e).join(", ")}`,
        severity: "warning",
      }),
    );
  };

  return (
    <SmartForm {...getSmartFormProps({})}>
      <Banners bannerDetails={createBannerDetails(showThdBanner)} />
      {powurFields.length > 0 && (
        <S.CustomFieldsContainer>
          <StyledSectionLabel>Custom Fields</StyledSectionLabel>
          <EnterpriseCustomFields customFields={powurFields} />
          <S.StyledDivider />
        </S.CustomFieldsContainer>
      )}
      {customFields.length > 0 && (
        <S.CustomFieldsContainer>
          <StyledSectionLabel>{enterpriseName} Fields</StyledSectionLabel>
          <EnterpriseCustomFields customFields={customFields} />
          <S.StyledDivider />
        </S.CustomFieldsContainer>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>
            Confirm Location {!leadDetails.isAddressConfirmed && <S.Red>*</S.Red>}
          </S.StyledSectionLabel>
          {!leadDetails.isAddressConfirmed && isMobileWidth && (
            <S.DarkBlueNote>Move pin to exact location</S.DarkBlueNote>
          )}
        </Grid>
        <Grid item xs={12}>
          <MapDisplay
            lead={leadDetails}
            getGeocode={actions.getLeadToProposalDrawerGeocode}
            setGeocode={actions.setLeadToProposalDrawerGeocode}
            latestMessage={latestMessage}
          />
        </Grid>
        <S.ImageUploadContainer>
          <S.ImageUploadNote isUploaderOpen={isImageUploadOpen}>
            New construction or map imagery missing?
            <S.ImageUploadClickable
              tabIndex={0}
              role="button"
              onKeyDown={() => setIsImageUploadOpen(!isImageUploadOpen)}
              onClick={() => setIsImageUploadOpen(!isImageUploadOpen)}
            >
              Upload custom images
              <S.ClickableCaret isUploaderOpen={isImageUploadOpen}>
                {isImageUploadOpen ? (
                  <DownCaretThicker fill={theme.colors["blue-ribbon"]} width="12" />
                ) : (
                  <RightCaretIcon width={16} height={16} />
                )}
              </S.ClickableCaret>
            </S.ImageUploadClickable>
          </S.ImageUploadNote>
          {isImageUploadOpen && (
            <Grid item xs={12}>
              <S.FormFieldLabel>Upload Custom Images</S.FormFieldLabel>
              <SingleFileDragAndDrop
                apiMode="lead_asset"
                fileUrl={getValues("customSiteImage")}
                setFileUrl={handleSiteImageUpload}
                fileSize={getValues("customSiteImageSize")}
                setFileSize={(bytes) => setValue("customSiteImageSize", bytes)}
              />
            </Grid>
          )}
        </S.ImageUploadContainer>
        <Grid item xs={12} sm={6}>
          <SmartFormDropdown
            name="mounting"
            mainLabel="Mounting Preference"
            dropdownOptions={mountingOptions}
            error={!!errors.mounting}
            helperText={errors.mounting?.message}
          />
        </Grid>
      </Grid>
      <S.StyledDivider />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>Utility Bill Information</S.StyledSectionLabel>
        </Grid>
        <Grid item xs={12} sm={6}>
          <SmartFormDropdown
            name="utilityCompany"
            mainLabel="Utility Company"
            dropdownOptions={utilityChoices}
            error={!!errors.utilityCompany}
            helperText={errors.utilityCompany?.message}
            required
            placeholder="Select..."
            disabled={!utilities.length}
          />
        </Grid>
        <Grid item xs={12}>
          <S.DarkBlueLabel>
            What is the homeowner&apos;s energy usage? <S.Red>*</S.Red>
          </S.DarkBlueLabel>
          <S.ItalicNote>Only one response is required.</S.ItalicNote>
        </Grid>
        <Grid item xs={12}>
          <S.EnergyUsageInputContainer container spacing={2}>
            <Grid item xs={12} sm>
              <SmartFormTextField
                label="Annual kWh consumption"
                name="kwhConsumption"
                placeholder="kWh"
                error={!!errors.kwhConsumption}
                helperText={errors.kwhConsumption?.message}
                onFocus={() => setHasBlueHighlightOnKwh(true)}
                hasBlueHighlight={highlightEnergyUsageInput && hasBlueHighlightOnKwh}
              />
            </Grid>
            {!isQualified && (
              <S.StyledOrRow item xs={12} sm="auto">
                OR
              </S.StyledOrRow>
            )}
            <Grid item xs={12} sm>
              <SmartFormTextField
                label="Average Monthly Bill"
                name="avgMonthlyBill"
                placeholder="0"
                error={!!errors.avgMonthlyBill}
                helperText={errors.avgMonthlyBill?.message}
                fieldPrefix="$"
                onFocus={() => setHasBlueHighlightOnKwh(false)}
                hasBlueHighlight={highlightEnergyUsageInput && !hasBlueHighlightOnKwh}
              />
            </Grid>
          </S.EnergyUsageInputContainer>
        </Grid>
        <Grid item xs={12}>
          <S.FormFieldLabel>Utility Bill</S.FormFieldLabel>
          <SingleFileDragAndDrop
            apiMode="lead_utility_bill"
            fileUrl={getValues("utilityBillUrl")}
            setFileUrl={(fileUrl) => setValue("utilityBillUrl", fileUrl, { shouldDirty: true })}
            fileSize={getValues("utilityBillSize")}
            setFileSize={(bytes) => setValue("utilityBillSize", bytes, { shouldDirty: true })}
          />
        </Grid>
      </Grid>
      <S.StyledDivider />
      <Grid container rowSpacing={3} columnSpacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>Existing System</S.StyledSectionLabel>
          <S.ExistingSystemLabel>
            Does this property have an existing solar system? <S.Red>*</S.Red>
          </S.ExistingSystemLabel>
        </Grid>
        <Grid item xs={12} sx={{ marginTop: "-24px" }}>
          <SmartFormRadioGroup
            name="hasExistingSystem"
            radioOptions={yesOrNoOptions}
            row
            radioLabelStyle={{ marginLeft: "-2px", marginRight: "14px" }}
          />
        </Grid>
        {stringToBool(getValues("hasExistingSystem")) && (
          <>
            <Grid item xs={12} sx={{ marginTop: "-8px" }}>
              <S.ExistingSystemLabel>
                Who owns the existing solar system? <S.Red>*</S.Red>
              </S.ExistingSystemLabel>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "-24px" }}>
              <SmartFormRadioGroup
                name="existingSystemOwnership"
                radioOptions={existingSystemOwnershipOptions}
                radioLabelStyle={{ marginLeft: "-2px", marginRight: "14px" }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <S.StyledDivider />
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid item xs={12}>
          <S.StyledSectionLabel>New Construction</S.StyledSectionLabel>
          <S.ExistingSystemLabel>
            Is there (non-solar) new construction in progress? <S.Red>*</S.Red>
          </S.ExistingSystemLabel>
        </Grid>
        <Grid item xs={12} sx={{ marginTop: "-16px" }}>
          <SmartFormRadioGroup
            name="isNewConstruction"
            radioOptions={yesOrNoOptions}
            row
            radioLabelStyle={{ marginLeft: "-2px", marginRight: "14px" }}
          />
        </Grid>
        {convertToBool(getValues("isNewConstruction")) && (
          <>
            <Grid item xs={6}>
              <S.FormFieldLabel>Estimated Completion Date</S.FormFieldLabel>
              <SmartFormDatePicker
                name="estimatedDateTo"
                renderInput={renderInput}
                onSubmit={handleSelectDate}
                displayValue={getValues("estimatedDateTo") || "Select Date"}
                hideTime
                error={!!errors.estimatedDateTo}
                helperText={errors.estimatedDateTo?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <SmartFormTextarea
                name="detailsOfConstruction"
                subLabel="Details of type of construction"
                subLabelStyle={{
                  textTransform: "uppercase",
                  fontSize: "12px",
                  fontWeight: "600",
                  lineHeight: "10px",
                  marginBottom: "12px",
                }}
                height={132}
              />
            </Grid>
          </>
        )}
      </Grid>
      <button ref={submitRef} style={{ display: "none" }} type="button" onClick={handleSubmit(onSubmit, onError)} />
      <button ref={saveRef} style={{ display: "none" }} type="button" onClick={handleUpdateLead} />
    </SmartForm>
  );
};

export default SiteInfoForm;
